
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { downloadFile, addBorderAroundTable, addStylingToHorizontalHeader, setCellFontSizeAndAlignement } from '@/utils/exceljs-helpers';
import * as ExcelJS from 'exceljs';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import DatePicker from '@/components/shared/DatePicker.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';

@Component<ImportOrdersModal>({
	components: {
		StandardModal,
		InfoBanner,
		DatePicker,
		WarningModal
	}
})
export default class ImportOrdersModal extends Vue {
	@Action('submitRepeatingOrders', { namespace: 'orders' }) private submitRepeatingOrders!: (payload: { file: File; startDate: string }) => Promise<void>;
	@Getter('getRestaurants', { namespace: 'auth' }) private restaurants!: Tenant[];
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;

	private isLoading: boolean = false;
	private file: File | null = null;
	private startDate: string = '';
	private warningModalProps: WarningModalProps = {
		open: false,
		title: '',
		text: '',
		confirmButtonText: '',
		cancelButtonText: '',
		onConfirm: () => {},
		onClose: () => {},
		onCancel: () => {}
	};


	private get isFileXlsx(): boolean {
		if (!this.file) {
			return false;
		}
		return this.file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || this.file.name.endsWith('.xlsx');
	}

	private get isFileTooLarge(): boolean {
		if (!this.file) {
			return false;
		}
		return this.file.size > 10 * 1024 * 1024; // 10 MB
	}

	// Minimum date to start the orders, set to 7 days after today
	private get minDate(): string {
		return DateTime.now().setZone(this.restaurant.menu_configuration?.zone || 'America/Toronto').plus({ day: 7 }).toFormat('yyyy-MM-dd');
	}

	/**
	 * Export a template excel file of the orders import file.
	 *
	 * @return {void}
	 */
	private exportTemplate(): void {
		const workbook = new ExcelJS.Workbook();

		this.createOverviewExcelTab(workbook);
		// Create a tab for each of the user's locations
		this.restaurants.forEach(restaurant => this.createLocationExcelTab(workbook, restaurant.slug));

		workbook.xlsx.writeBuffer().then(buffer => {
			downloadFile(buffer, 'orders_import_template.xlsx');
		});
	}

	/**
	 * Create the overview tab in the Excel file.
	 *
	 * @param {ExcelJS.Workbook} workbook
	 * @return {void}
	 */
	private createOverviewExcelTab(workbook: ExcelJS.Workbook): void {
		const worksheet = workbook.addWorksheet('Overview', {
			pageSetup: { orientation: 'landscape' }
		});

		worksheet.columns = [{ width: 35 }, { width: 150 }];

		const warningRow = worksheet.addRow([
			this.$t('orders.management.import.spreadsheet.warning_header'),
			''
		]);
		warningRow.height = 30;

		// Overview table starting at row 3
		const overviewData = [
			[this.$t('orders.management.import.spreadsheet.section_header'), this.$t('orders.management.import.spreadsheet.details_header')],
			[this.$t('orders.management.import.spreadsheet.purpose_header'), this.$t('orders.management.import.spreadsheet.purpose_description')],
			[this.$t('orders.management.import.spreadsheet.instructions_header'), this.$t('orders.management.import.spreadsheet.instruction_1')],
			['', this.$t('orders.management.import.spreadsheet.instruction_2')],
			['', this.$t('orders.management.import.spreadsheet.instruction_3')],
			['', this.$t('orders.management.import.spreadsheet.instruction_4')],
			['', this.$t('orders.management.import.spreadsheet.instruction_5')],
			['', this.$t('orders.management.import.spreadsheet.instruction_6')],
			['', this.$t('orders.management.import.spreadsheet.instruction_7')]
		];

		worksheet.addRows(overviewData);

		setCellFontSizeAndAlignement(worksheet, 14, { vertical: 'top', horizontal: 'left' });
		addStylingToHorizontalHeader(worksheet, 2);
		addBorderAroundTable(worksheet, 2, worksheet.rowCount);

		['B5', 'B6', 'B9'].forEach(cell => {
			worksheet.getCell(cell).font = {
				...worksheet.getCell(cell).font,
				bold: true
			};
		});

		worksheet.mergeCells('A1:B1');
		const warningCell = worksheet.getCell('A1');

		warningCell.font = {
			bold: true,
			size: 16,
			color: { argb: 'FFFF0000' }
		};

		warningCell.alignment = {
			vertical: 'middle',
			horizontal: 'center'
		};

		warningCell.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFFFF2CC' }
		};
	}

	/**
	 * Create the orders import tab for a given location slug.
	 *
	 * @param {ExcelJS.Workbook} workbook
	 * @param {string} slug - Location slug used as tab name
	 * @return {void}
	 */
	private createLocationExcelTab(workbook: ExcelJS.Workbook, slug: string): void {
		const worksheet = workbook.addWorksheet(slug, {
			pageSetup: { orientation: 'landscape' }
		});

		// Set up columns with headers
		// * We do not localize the headers as Remy is expecting those to be in English for processing
		worksheet.columns = [
			{ header: 'Student name', width: 30 },
			{ header: 'Category', width: 20 },
			{ header: 'Grade', width: 15 },
			{ header: 'Class', width: 20 },
			{ header: 'Teacher', width: 30 },
			{ header: 'Allergies', width: 30 }
		];

		// Apply styling
		setCellFontSizeAndAlignement(worksheet, 14, { vertical: 'top', horizontal: 'left' });
		addStylingToHorizontalHeader(worksheet, undefined, {
			border: {
				top: { color: { argb: '000000' } },
				right: { color: { argb: '000000' } },
				bottom: { color: { argb: '000000' } },
				left: { color: { argb: '000000' } }
			}
		});
	}

	/**
	 * Submit the orders file for processing.
	 *
	 * @return {Promise<void>}
	 */
	private async handleSubmit(): Promise<void> {
		this.warningModalProps.open = false;

		if (!this.file || !this.isFileXlsx || this.isFileTooLarge || !this.startDate) {
			Toast.error(this.$t('orders.management.import.error_invalid_setup'));
			return;
		}

		this.isLoading = true;
		try {
			await this.submitRepeatingOrders({
				file: this.file,
				startDate: this.startDate
			});

			this.warningModalProps = {
				open: true,
				title: this.$t('orders.management.import.success_modal.title'),
				text: this.$t('orders.management.import.success_modal.description'),
				confirmButtonText: '',
				cancelButtonText: this.$t('orders.management.import.success_modal.close_btn'),
				onConfirm: () => {
					this.warningModalProps.open = false;
					this.$emit('close');
				},
				onClose: () => {
					this.warningModalProps.open = false;
					this.$emit('close');
				},
				onCancel: () => {
					this.warningModalProps.open = false;
					this.$emit('close');
				}
			};
			this.isLoading = false;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.import.error_submitting_file', { errorMessage }));

			this.warningModalProps = {
				open: true,
				title: this.$t('orders.management.import.error_modal.title'),
				text: this.$t('orders.management.import.error_modal.description'),
				confirmButtonText: this.$t('orders.management.import.error_modal.try_again_btn'),
				cancelButtonText: this.$t('shared.cancel'),
				onConfirm: () => {
					this.warningModalProps.open = false;
					this.handleSubmit();
				},
				onClose: () => {
					this.warningModalProps.open = false;
				},
				onCancel: () => {
					this.warningModalProps.open = false;
				}
			};
			this.isLoading = false;
		}
	}
}
